import { useState } from 'react';
import { TrackingService } from '../utility/services';
import { useDebouncedEffect } from './debounce';
import { CommonConstant } from '../utility/constants';
import { CommonUtility } from '../utility/common';
import type { ITrackingItem } from '@page-components/order/tracking';
import type { ISortState } from '@components/index';
import type { TSortDirection } from 'types';
import axios, { type CancelToken, type CancelTokenSource } from 'axios';

interface TrackingFilter {
  limit: number;
  page: number;
  search: string;
  carrier: string;
  category: string;
  sort: {
    sortBy: string;
    sortOrder: TSortDirection;
  };
}

interface TrackingHookParams {
  customerId?: string;
}

interface TrackingHookReturn {
  loading: boolean;
  data: ITrackingItem[];
  error: any;
  totalData: number;
  totalPages: number;
  filter: TrackingFilter;
  filterChanged: (key: string, value: any) => void;
  pageChanged: (page: number) => void;
  setSearch: (search: string) => void;
  onSortChange: (sortObject: ISortState) => void;
  parsedSort: ISortState;
  refresh: () => void;
  updateAllFilter: (filterObject: Partial<TrackingFilter>) => void;
}

export const TrackingHook = ({ customerId }: TrackingHookParams): TrackingHookReturn => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<ITrackingItem[]>([]);
  const [error, setError] = useState<any>(null);
  const [totalData, setTotalData] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [filter, setFilter] = useState<TrackingFilter>({
    limit: 50, // Default page size
    page: 1,
    search: '',
    carrier: '',
    category: '',
    sort: {
      sortBy: 'createdAt',
      sortOrder: CommonConstant.sortDirection.descending as TSortDirection,
    },
  });

  const fetchData = async (cancelToken?: CancelToken) => {
    setLoading(true);
    try {
      const trimmedSearch = (filter.search || '').trim();

      const params: any = {
        ...filter,
        ...filter.sort,
        sort: undefined,
        search: trimmedSearch || undefined,
        carrier: filter.carrier || undefined,
        customerOrganizationId: customerId || undefined,
      };

      if (!params.category) {
        delete params.category;
      }

      // Clean up undefined params
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      const res = await TrackingService.getTrackingList(params, cancelToken);
      setData((res?.data as ITrackingItem[]) || []);
      setTotalData(res?.totalCount || 0);
      setTotalPages(res?.totalPages || 0);
    } catch (error) {
      setError(error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      let request: CancelTokenSource | null = null;
      if (filter.limit) {
        request = axios.CancelToken.source();

        fetchData(request.token);
      }

      return () => {
        if (request) {
          request.cancel();
        }
      };
    },
    [filter, customerId, refreshKey],
    500,
  );

  const refresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const pageChanged = (page: number) => {
    setFilter(prev => ({
      ...prev,
      page,
    }));
  };

  const filterChanged = (key: string, value: any) => {
    setFilter(prev => ({
      ...prev,
      page: 1,
      [key]: value,
    }));
  };

  const setSearch = (search: string) => {
    filterChanged('search', search);
  };

  const onSortChange = (sortObject: ISortState) => {
    if (!CommonUtility.isValidObject(sortObject)) return;
    const sortKey = Object.keys(sortObject)[0];
    const sortValue = sortObject[sortKey];

    const temp: TrackingFilter = {
      ...filter,
      sort: {
        sortBy: sortKey,
        sortOrder: sortValue,
      },
    };
    setFilter(temp);
  };

  const parsedSort: ISortState = {
    [filter.sort.sortBy]: filter.sort.sortOrder,
  };

  const updateAllFilter = (filterObject: Partial<TrackingFilter>) => {
    const temp: TrackingFilter = {
      ...filter,
      page: 1,
      ...filterObject,
    };
    setFilter(temp);
  };

  return {
    loading,
    data,
    error,
    totalData,
    totalPages,
    filter,
    filterChanged,
    pageChanged,
    setSearch,
    onSortChange,
    parsedSort,
    refresh,
    updateAllFilter,
  };
};
